// WHAT: Model selector dropdown component for switching between available Ollama models
// WHY:  Provides UI for selecting and switching between different LLM models with availability indicators
// DOMAIN: LLM → Ollama → Model Management
// DEPENDENCIES: React state management, debugLog utility, UI components
// RISKS:
//   - NOTE: Fallback models list is hardcoded - may not reflect actual installed models
//   - NOTE: Model availability check is based on props - may not be real-time accurate
//   - WARN: Dropdown positioning uses absolute positioning - may overflow on small screens
//   - NOTE: Model selection callback may trigger expensive operations

import type { LogDomain } from "@/domains/logging/entities";

import { ChevronDown, Check, AlertCircle } from "lucide-react";
import { type FC, useState, useEffect } from "react";

import { Button, Stack, Text, Badge } from "@/components/ui";
import { debugLog } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Props interface for ModelSelector component.
 */
interface ModelSelectorProperties {
  /** List of available models from API */
  availableModels?: string[];
  /** Currently selected model (defaults to "deepseek-r1:8b") */
  currentModel?: string;
  /** Whether Ollama service is healthy (defaults to true) */
  isHealthy?: boolean;
  /** Callback when model is selected */
  onModelChange: (model: string) => void;
}

/**
 * WHAT:
 *   Dropdown component for selecting and switching between available Ollama models.
 *
 * WHY:
 *   Provides user interface for model selection with availability indicators and fallback options.
 *
 * PROPS:
 *   - currentModel?: string — Currently selected model (defaults to "deepseek-r1:8b")
 *   - availableModels?: string[] — List of available models from API
 *   - onModelChange: (model: string) => void — Callback when model is selected
 *   - isHealthy?: boolean — Whether Ollama service is healthy (defaults to true)
 *
 * BEHAVIOR:
 *   - Displays current model with health indicator
 *   - Opens dropdown menu on click showing all available models
 *   - Uses fallback model list if API fails to provide models
 *   - Shows availability status for each model
 *   - Closes dropdown on model selection or backdrop click
 *   - Logs model changes to debug system
 *
 * RISKS:
 *   - NOTE: Fallback models list is hardcoded - may not reflect actual installed models
 *   - NOTE: Model availability check is based on props - may not be real-time accurate
 *   - WARN: Dropdown positioning uses absolute positioning - may overflow on small screens
 *   - NOTE: Model selection callback may trigger expensive operations
 *
 * @param root0
 *
 * @param root0.currentModel
 *
 * @param root0.availableModels
 *
 * @param root0.onModelChange
 *
 * @param root0.isHealthy
 *
 * @returns JSX element with model selector dropdown
 *
 */
export const ModelSelector: FC<ModelSelectorProperties> = ({
  availableModels,
  currentModel = "deepseek-r1:8b",
  isHealthy = true,
  onModelChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const safeAvailableModels = Array.isArray(availableModels) ? availableModels : [];
  const [models, setModels] = useState<string[]>(safeAvailableModels);

  useEffect(() => {
    if (Array.isArray(availableModels) && availableModels.length > 0) {
      setModels(availableModels);
    }
  }, [availableModels]);

  /**
   * WHAT:
   *   Handles model selection from dropdown menu.
   *
   * WHY:
   *   Centralizes model change logic with logging and UI state management.
   *
   * SIDE EFFECTS:
   *   - Logs model change to debug system
   *   - Calls onModelChange callback
   *   - Closes dropdown menu
   *
   * @param model
   *
   */
  const handleModelSelect = (model: string) => {
    debugLog("info", "llm" as LogDomain, `ModelSelector: Switching to model: ${model}`);
    onModelChange(model);
    setIsOpen(false);
  };

  // Common model list if API fails
  const fallbackModels = [
    "deepseek-r1:8b",
    "deepseek-r1:14b",
    "deepseek-r1:32b",
    "llama3.2:3b",
    "qwen2.5:7b",
    "mistral:7b",
  ];

  const displayModels = models.length > 0 ? models : fallbackModels;

  return (
    <div className="relative">
      {/* Trigger Button */}
      <Button
        className="gap-2 min-w-[200px] justify-between"
        disabled={!isHealthy}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        size="sm"
        variant="outline"
      >
        <Stack align="center" direction="row" gap={2}>
          <div className={`w-2 h-2 rounded-full ${isHealthy ? "bg-green-400" : "bg-red-400"}`} />
          <Text className="text-sm font-medium truncate max-w-[140px]">{currentModel}</Text>
        </Stack>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </Button>

      {/* Dropdown Menu */}
      {isOpen ? (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setIsOpen(false);
            }}
          />

          {/* Menu */}
          <div className="absolute top-full mt-2 right-0 z-50 min-w-[280px] bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/50">
              <Text className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                Select Model
              </Text>
            </div>

            {/* Model List */}
            <div className="max-h-[300px] overflow-y-auto">
              {displayModels.length === 0 ? (
                <div className="p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <Text className="text-sm text-slate-400">No models available</Text>
                  <Text className="text-xs text-slate-500 mt-1">
                    Run: ollama pull deepseek-r1:8b
                  </Text>
                </div>
              ) : (
                displayModels.map((model) => {
                  const isSelected = model === currentModel;
                  const isAvailable = models.includes(model);

                  return (
                    <button
                      className={`
                        w-full px-4 py-3 flex items-center justify-between
                        transition-colors
                        ${
                          isSelected
                            ? "bg-blue-500/10 text-blue-400"
                            : "text-slate-300 hover:bg-slate-700/50"
                        }
                        ${isAvailable ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}
                      `}
                      disabled={!isAvailable}
                      key={model}
                      onClick={() => {
                        handleModelSelect(model);
                      }}
                    >
                      <Stack align="center" direction="row" gap={3}>
                        <div
                          className={`w-2 h-2 rounded-full ${isAvailable ? "bg-green-400" : "bg-slate-600"}`}
                        />
                        <div className="text-left">
                          <Text className="text-sm font-medium">{model}</Text>
                          {!isAvailable && (
                            <Text className="text-xs text-slate-500 mt-0.5">Not installed</Text>
                          )}
                        </div>
                      </Stack>

                      {isSelected ? <Check className="w-4 h-4 text-blue-400" /> : null}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {models.length === 0 && (
              <div className="px-4 py-3 border-t border-slate-700 bg-slate-800/50">
                <Badge className="text-xs" variant="warning">
                  Install models with: ollama pull [model-name]
                </Badge>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};
